import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowRight, Sparkles, Gift, Printer, Mail } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { Logo } from "@/components/Logo";
import { fetchGiftPlans, createGiftCheckout } from "@/lib/api";
import { toast } from "sonner";

const AcademyGift = () => {
  const nav = useNavigate();
  const [plans, setPlans] = useState({});
  const [planKey, setPlanKey] = useState("founding_1mo");
  const [form, setForm] = useState({
    sender_name: "",
    sender_email: "",
    recipient_name: "",
    recipient_email: "",
    personal_message: "",
    delivery: "email_now", // email_now | self_deliver
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchGiftPlans().then(setPlans).catch(() => setPlans({}));
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.sender_name || !form.sender_email || !form.recipient_name) {
      toast.error("Please fill in your details and the recipient's name.");
      return;
    }
    if (form.delivery === "email_now" && !form.recipient_email) {
      toast.error("Please add the recipient's email — or switch to 'I'll deliver myself'.");
      return;
    }
    setLoading(true);
    try {
      const { checkout_url } = await createGiftCheckout({
        plan_key: planKey,
        origin_url: window.location.origin,
        sender_name: form.sender_name.trim(),
        sender_email: form.sender_email.trim(),
        recipient_name: form.recipient_name.trim(),
        recipient_email: form.delivery === "email_now" ? form.recipient_email.trim() : null,
        personal_message: form.personal_message.trim(),
        deliver_now: form.delivery === "email_now",
      });
      window.location.href = checkout_url;
    } catch {
      toast.error("Unable to start checkout. Please try again.");
      setLoading(false);
    }
  };

  const planOrder = ["founding_1mo", "standard_1mo", "standard_3mo", "standard_12mo"];

  return (
    <main className="bg-cream min-h-screen pt-32" data-testid="academy-gift-page">
      <section className="bg-emerald text-cream py-20 md:py-24">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10">
          <Reveal>
            <p className="eyebrow text-gold mb-6 flex items-center gap-3">
              <Gift className="w-4 h-4" /> Gift a Membership
            </p>
            <h1 className="font-display text-5xl md:text-6xl leading-[0.95] tracking-tight max-w-3xl">
              Give the gift of <span className="italic text-gold">flourishing.</span>
            </h1>
            <p className="font-editorial italic text-2xl text-cream/80 mt-6 max-w-2xl">
              A month, a season, or a full year of The Love Genome™ Academy — delivered by email or by your own hand.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1200px] mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-12">
          {/* Plan selector */}
          <div>
            <p className="eyebrow text-emerald mb-4">1. Choose your gift</p>
            <div className="space-y-3">
              {planOrder.map((k) => {
                const p = plans[k];
                if (!p) return null;
                const selected = planKey === k;
                return (
                  <button
                    key={k}
                    type="button"
                    onClick={() => setPlanKey(k)}
                    data-testid={`gift-plan-${k}`}
                    className={`w-full flex items-center gap-4 p-5 text-left border transition-colors ${
                      selected ? "bg-emerald text-cream border-emerald" : "bg-surface border-line hover:border-emerald/40"
                    }`}
                  >
                    <span
                      className={`w-5 h-5 shrink-0 border flex items-center justify-center ${
                        selected ? "bg-gold border-gold" : "border-line"
                      }`}
                    >
                      {selected && <Check className="w-3 h-3 text-ink" strokeWidth={3} />}
                    </span>
                    <div className="flex-1">
                      <p className={`font-display text-lg ${selected ? "text-cream" : "text-ink"}`}>
                        {p.months} month{p.months !== 1 ? "s" : ""} · {p.name}
                      </p>
                      <p className={`font-body text-xs mt-1 ${selected ? "text-cream/70" : "text-ink/55"}`}>
                        {k === "founding_1mo" && "Founding Member rate — locked in for life if the recipient continues."}
                        {k === "standard_1mo" && "One month of full Standard access."}
                        {k === "standard_3mo" && "One season of monthly lessons, Q&A, and tools."}
                        {k === "standard_12mo" && "A full year — the deepest transformation."}
                      </p>
                    </div>
                    <span className={`font-display text-2xl ${selected ? "text-gold" : "text-emerald"}`}>
                      ${p.price}
                    </span>
                  </button>
                );
              })}
            </div>

            <div className="mt-10 p-6 border border-line bg-surface">
              <p className="eyebrow text-emerald mb-3 flex items-center gap-2">
                <Sparkles className="w-4 h-4" /> What the recipient gets
              </p>
              <ul className="space-y-2 text-sm font-body text-ink/80">
                <li>• Full Academy access for the gifted duration</li>
                <li>• Live monthly Q&amp;A and masterclass invites</li>
                <li>• Downloadable worksheets, prompts, and assessments</li>
                <li>• Access to the member-only community</li>
              </ul>
            </div>
          </div>

          {/* Details + delivery */}
          <form onSubmit={submit} className="space-y-6" data-testid="gift-form">
            <div>
              <p className="eyebrow text-emerald mb-4">2. Your details</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  data-testid="gift-sender-name"
                  className="input"
                  placeholder="Your name"
                  value={form.sender_name}
                  onChange={(e) => set("sender_name", e.target.value)}
                />
                <input
                  data-testid="gift-sender-email"
                  type="email"
                  className="input"
                  placeholder="Your email (for the receipt)"
                  value={form.sender_email}
                  onChange={(e) => set("sender_email", e.target.value)}
                />
              </div>
            </div>

            <div>
              <p className="eyebrow text-emerald mb-4">3. Recipient</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  data-testid="gift-recipient-name"
                  className="input"
                  placeholder="Recipient's name"
                  value={form.recipient_name}
                  onChange={(e) => set("recipient_name", e.target.value)}
                />
                <input
                  data-testid="gift-recipient-email"
                  type="email"
                  className="input"
                  placeholder="Recipient's email (optional)"
                  value={form.recipient_email}
                  onChange={(e) => set("recipient_email", e.target.value)}
                  disabled={form.delivery === "self_deliver"}
                />
              </div>
              <textarea
                data-testid="gift-personal-message"
                className="input mt-3 min-h-[100px]"
                placeholder="A short personal note (optional)"
                maxLength={500}
                value={form.personal_message}
                onChange={(e) => set("personal_message", e.target.value)}
              />
            </div>

            <div>
              <p className="eyebrow text-emerald mb-4">4. How should we deliver?</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => set("delivery", "email_now")}
                  data-testid="gift-deliver-email"
                  className={`p-4 border text-left transition-colors ${
                    form.delivery === "email_now"
                      ? "bg-emerald text-cream border-emerald"
                      : "bg-surface border-line hover:border-emerald/40"
                  }`}
                >
                  <Mail className="w-5 h-5 mb-2" />
                  <p className="font-display text-base">Email the recipient</p>
                  <p className={`font-body text-xs mt-1 ${form.delivery === "email_now" ? "text-cream/70" : "text-ink/55"}`}>
                    We'll send them a beautiful gift email with the claim link the moment payment clears.
                  </p>
                </button>
                <button
                  type="button"
                  onClick={() => set("delivery", "self_deliver")}
                  data-testid="gift-deliver-self"
                  className={`p-4 border text-left transition-colors ${
                    form.delivery === "self_deliver"
                      ? "bg-emerald text-cream border-emerald"
                      : "bg-surface border-line hover:border-emerald/40"
                  }`}
                >
                  <Printer className="w-5 h-5 mb-2" />
                  <p className="font-display text-base">I'll deliver it myself</p>
                  <p className={`font-body text-xs mt-1 ${form.delivery === "self_deliver" ? "text-cream/70" : "text-ink/55"}`}>
                    You'll get a printable PDF gift certificate (with claim code) to hand-deliver or send yourself.
                  </p>
                </button>
              </div>
              <p className="text-xs text-ink/50 font-body mt-3">
                You'll always receive a printable PDF certificate too — a keepsake for either delivery method.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-gold w-full justify-center disabled:opacity-60"
              data-testid="gift-submit"
            >
              {loading ? "Redirecting…" : <>Give the Gift <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-center text-[11px] text-ink/45 font-body">
              Secure billing via Stripe. One-time payment — no auto-renewal for the recipient.
            </p>

            <button
              type="button"
              onClick={() => nav("/academy")}
              className="w-full text-center font-body text-xs text-ink/50 hover:text-emerald underline underline-offset-4"
            >
              Prefer a subscription for yourself? See Academy plans
            </button>
          </form>
        </div>
      </section>
    </main>
  );
};

export default AcademyGift;
